import DeleteIcon from "@mui/icons-material/Delete";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import DoneIcon from "@mui/icons-material/Done";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import swal from "sweetalert";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomTable from "../../components/CustomTable";
import MainLayout from "../../components/MainLayout";
import {
  LeftPane,
  RightPane,
  SplitViewContainer,
} from "../../components/SplitView";
import axiosInstance from "../../utils/axiosInstance";
import { checkPermission } from "../../utils/checkPermission";
import { formatCustomDate } from "../../utils/formatters";

export default function UserQuestions() {
  const [refresh, setRefresh] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState();
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleModalClose = () => {
    setOpenConfirm(false);
  };

  const handleDeleteQuestion = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/questions/${selectedQuestion.id}`,
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        setSelectedQuestion(null);
        handleModalClose();
        swal("Question deleted successfully!", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to delete question. Please try again.", { icon: "error" });
    }
  };

  const handleDeleteAnswer = async (answerId) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/answers/${answerId}`,
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        setSelectedQuestion(null);
        swal("Answer deleted successfully!", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to delete answer. Please try again.", { icon: "error" });
    }
  };

  const approveOrReject = async (id, is_approved) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/answers/${id}/approve-or-reject`,
        {
          is_approved,
        },
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        setSelectedQuestion(null);
        swal("Question status updated successfully.", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to  updated", { icon: "error" });
    }
  };

  const columns = [
    {
      field: "profile_picture",
      headerName: "Avatar",
      width: 70,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {value.row.user && (
            <Avatar
              src={`${process.env.REACT_APP_STORAGE_URL}/${value.row.user.profile_picture}`}
              alt={`${value.row.user.full_name.slice(0).toUpperCase()}`}
              sx={{ width: "24px", height: "24px" }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "user",
      headerName: "User",
      width: 200,
      valueGetter: (value) => `${value.full_name}`,
    },
    { field: "title", headerName: "Title", width: 400 },
    {
      field: "post_date",
      headerName: "Post Date",
      width: 150,
      valueGetter: (value) => value && new Date(value),
      renderCell: (value) => formatCustomDate(value.row.post_date),
      type: "date",
    },
    {
      field: "is_approved",
      headerName: "Is Approved?",
      width: 100,
      type: "boolean",
      sortable: false,
      renderCell: (params) => {
        const handleToggleApproval = async () => {
          try {
            const newApprovalStatus = !params.row.is_approved;
            const response = await axiosInstance.patch(
              `${process.env.REACT_APP_API_URL}/questions/${params.row.id}/approve-or-reject`,
              { is_approved: newApprovalStatus },
            );

            if (response.status === 200) {
              setRefresh(!refresh); // Refresh and keep the selected question
              swal("Approval status updated successfully!", {
                icon: "success",
              });
            }
          } catch (error) {
            swal("Failed to update approval status. Please try again.", {
              icon: "error",
            });
          }
        };

        return (
          <Tooltip title="Toggle Approval">
            <IconButton onClick={handleToggleApproval} color="primary">
              {params.value ? <DoneIcon /> : <DoDisturbIcon />}
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "answers",
      headerName: "Answers",
      width: 100,
      valueGetter: (value) => value.length,
      type: "number",
    },
    {
      field: "",
      headerName: "Actions",
      width: 200,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          <Tooltip
            title={
              checkPermission("answer-view")
                ? "List answers"
                : "You are not allowed to view answers"
            }
          >
            <Button
              color="info"
              disabled={!checkPermission("answer-view")}
              onClick={() => {
                setSelectedQuestion(value.row);
              }}
            >
              View Answers
            </Button>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <MainLayout>
        <Typography variant="h6">User Questions</Typography>

        <SplitViewContainer>
          <LeftPane>
            <CustomTable
              columns={columns}
              fetchDataUrl="questions-paginated"
              refresh={refresh}
            />
          </LeftPane>

          <Divider orientation="vertical" />

          <RightPane>
            {selectedQuestion ? (
              <>
                <Typography variant="h6">{`Q. ${selectedQuestion.title}`}</Typography>
                <Typography color="textSecondary">{`Asked by ${selectedQuestion.user.full_name} on ${formatCustomDate(selectedQuestion.post_date)}`}</Typography>
                {selectedQuestion.answers.length > 0 ? (
                  <List>
                    {selectedQuestion.answers.map((answer) => {
                      return (
                        <ListItem key={answer.id}>
                          <ListItemAvatar>
                            <Avatar>{answer.user.full_name.charAt(0)}</Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={answer.answer}
                            secondary={`Answered by ${answer.user.full_name} on ${formatCustomDate(answer.post_date)}`}
                          />
                          {checkPermission("answer-update") && (
                            <>
                              {answer.is_approved !== true && (
                                <Tooltip title="Approve">
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      approveOrReject(answer.id, true)
                                    }
                                  >
                                    <DoneIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {(answer.is_approved === true ||
                                answer.is_approved === null) && (
                                <Tooltip title="Reject">
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      approveOrReject(answer.id, false)
                                    }
                                  >
                                    <DoDisturbIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                          {checkPermission("answer-delete") && (
                            <Tooltip title="Delete">
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteAnswer(answer.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Box
                    sx={{
                      pt: 4,
                    }}
                  >
                    <Typography variant="subtitle">
                      There are no answers just yet.
                    </Typography>
                  </Box>
                )}

                {checkPermission("question-delete") && (
                  <Button
                    color="error"
                    variant="contained"
                    sx={{ my: 2 }}
                    onClick={() => setOpenConfirm(true)}
                  >
                    Delete Question
                  </Button>
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography variant="subtitle">
                  Select a question to view answers.
                </Typography>
              </Box>
            )}
          </RightPane>
        </SplitViewContainer>
      </MainLayout>

      {openConfirm && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={handleDeleteQuestion}
          title={`Are you sure you want to delete this user question?`}
          message={
            <span>
              The question will be delete. Note that this action is not
              reversible.
            </span>
          }
        />
      )}
    </>
  );
}
